// Activity-log table columns — the domain cells inside the common DataTable:
// actor avatar + name, the action chip, the module-tiled description, and the
// relative timestamp. NOTHING is sortable: the confirmed list body carries no
// orderBy, so no header may pretend otherwise.
//
// `description` is a ready-to-display sentence from the wire, rendered
// literally (no i18n keys for backend data). The action chip maps every slug of
// the confirmed ActivityLogAction enum through the complete ACTION_META.

import { useTranslation } from 'react-i18next';
import { Avatar, Chip, Icon, type DataTableColumn } from '@/components/common';
import type { ActivityItem } from '@/api/endpoints/activityLogApi';
import toneOf from '@/utils/avatarTone';
import initialsOf from '@/utils/initials';
import {
  ACTION_META,
  MODULE_ICONS,
  activityDateParts,
  entityLabel,
  targetCode,
  targetName,
} from './activityMeta';

const dash = <span className='activity-table__dash'>—</span>;

export const useActivityColumns = (): DataTableColumn<ActivityItem>[] => {
  const { t } = useTranslation();

  return [
    {
      key: 'user',
      label: t('activity_log_col_user'),
      width: 'minmax(12rem, 1fr)',
      render: (a) =>
        a.user ? (
          <span className='activity-table__person'>
            <Avatar
              initials={initialsOf(a.user.fullName)}
              size={32}
              tone={toneOf(a.user.fullName)}
            />
            <span className='activity-table__person-text'>
              <span className='activity-table__person-name'>{a.user.fullName}</span>
              {a.user.title && <span className='activity-table__person-title'>{a.user.title}</span>}
            </span>
          </span>
        ) : (
          dash
        ),
    },
    {
      key: 'action',
      label: t('activity_log_col_action'),
      render: (a) => (
        <Chip variant={ACTION_META[a.action].variant}>{t(ACTION_META[a.action].labelKey)}</Chip>
      ),
    },
    {
      key: 'target',
      label: t('activity_log_col_target'),
      width: 'minmax(16rem, 2fr)',
      render: (a) => (
        <span className='activity-table__target'>
          <span className='activity-table__tile' aria-hidden='true'>
            <Icon name={MODULE_ICONS[a.module] ?? 'scroll-text'} size={16} />
          </span>
          <span className='activity-table__target-text'>
            <span className='activity-table__target-name'>{targetName(a)}</span>
            <span className='activity-table__target-sub'>
              {entityLabel(a.module, t)}
              {a.entityReferenceNumber != null && (
                <>
                  <span className='activity-table__target-dot'>·</span>
                  {targetCode(a.module, String(a.entityReferenceNumber), targetName(a))}
                </>
              )}
            </span>
          </span>
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: t('activity_log_col_date'),
      align: 'end',
      render: (a) => {
        const { day, time } = activityDateParts(a.createdAt, t);
        return (
          <span className='activity-table__date'>
            <span className='activity-table__date-day'>{day}</span>
            {time && <span className='activity-table__date-time'>{time}</span>}
          </span>
        );
      },
    },
  ];
};
